import { useState } from 'react';
import { Navigate, Outlet, useMatch, useLocation } from 'react-router-dom';
import { alpha, useTheme } from '@mui/material/styles';
import Collapse from '@mui/material/Collapse';
import Paper from '@mui/material/Paper';
import List from '@mui/material/List';
import { Box, Flex } from '@atoms/layout';
import { SettingsMenuItem } from '@molecules/navigation';
import { SettingsPagePane } from './components';
import Container from '@mui/material/Container';

export function SettingsRoot() {
  const match = useMatch('/settings');
  const location = useLocation();
  const theme = useTheme();
  const [opened, setOpened] = useState<Record<string, boolean | undefined>>(getOpenedByLocation(location.pathname));
  const toggleOpened = (id: string) => {
    setOpened((prev) => {
      if (prev[id]) {
        return { ...prev, [id]: false };
      }
      return { ...prev, [id]: true };
    });
  };

  if (match) {
    // Redirect to default sub page
    return <Navigate to="/settings/general" replace={true} />;
  }

  return (
    <Container maxWidth={'xl'}>
      <Paper sx={{ overflow: 'hidden', maxHeight: '100%', display: 'flex' }}>
        <Flex width="100%">
          <Box p={3} bgcolor={alpha(theme.palette.primary.main, 0.08)} flexShrink={0}>
            <List>
              {settingsMenu.map((menu) => (
                <Box key={menu.id}>
                  <SettingsMenuItem
                    opened={opened[menu.id]}
                    hasChildren={menu.children.length > 0}
                    titleKey={menu.titleKey}
                    to={menu.to}
                    onClick={() => toggleOpened(menu.id)}
                  />
                  {menu.children.length > 0 && (
                    <Collapse in={opened[menu.id]} timeout="auto">
                      <List disablePadding>
                        {menu.children.map((child) => (
                          <SettingsMenuItem
                            key={child.id}
                            opened={opened[child.id]}
                            hasChildren={child.children.length > 0}
                            titleKey={child.titleKey}
                            to={child.to}
                            onClick={() => toggleOpened(child.id)}
                            sx={{ pl: 4 }}
                            dense
                          />
                        ))}
                      </List>
                    </Collapse>
                  )}
                </Box>
              ))}
            </List>
          </Box>
          <Box width="100%" overflow="auto">
            <SettingsPagePane>
              <Outlet />
            </SettingsPagePane>
          </Box>
        </Flex>
      </Paper>
    </Container>
  );
}

function getOpenedByLocation(location: string) {
  const locationParts = location.split('/').filter(Boolean);
  return locationParts.reduce<Record<string, boolean>>((acc, part) => {
    acc[part] = true;
    return acc;
  }, {});
}

type MenuItem = {
  id: string;
  titleKey: string;
  children: MenuItem[];
  to: string;
};
const settingsMenu: MenuItem[] = [
  {
    id: 'organization',
    titleKey: 'organization',
    to: '/settings/organization/details',
    children: [
      {
        id: 'details',
        titleKey: 'organizationDetails',
        to: '/settings/organization/details',
        children: [],
      },
      {
        id: 'documents',
        titleKey: 'documents',
        to: '/settings/organization/documents',
        children: [],
      },
      {
        id: 'branding',
        titleKey: 'branding',
        to: '/settings/organization/branding',
        children: [],
      },
    ],
  },
];
